import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './CopytradeTab.css';
import Badge from './components/Badge';
import LiveDot from './components/LiveDot';
import StatCard from './components/StatCard';
import ModalOverlay from './components/ModalOverlay';
import { useBotWallet } from './useBotWallet';
import { botEngine, getBotStats } from './bot/botEngine';
import { getBotLogs } from './bot/botLogger';
import { supabase } from './supabase';
import { useManualTrade, TxMsg } from './useManualTrade';
import { calcAnalytics, loadAndMergeTradesFromSupabase } from './tradeUtils';
import { useEffectiveUser } from './ImpersonationContext';

const STORAGE_KEY = 'copytrade-configs';
const INITIAL_CONFIGS = [];
let nextConfigId = 6;

function loadConfigs() {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (!s) return INITIAL_CONFIGS;
    const configs = JSON.parse(s);
    return configs.map(c => { const { mode, autoSell, ...rest } = c; return rest; });
  } catch { return INITIAL_CONFIGS; }
}

async function loadConfigsFromSupabase(overrideUserId = null) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    const targetId = overrideUserId || session.user.id;
    const { data, error } = await supabase.from('configs').select('*').eq('user_id', targetId).order('created_at', { ascending: true });
    if (error || !data || data.length === 0) return null;
    return data.map((c, i) => ({
      id: c.id || (nextConfigId + i),
      name: c.name,
      emoji: c.emoji || '',
      targetWallet: c.target_wallet,
      botWalletId: c.bot_wallet_id,
      amountMode: c.amount_mode,
      amountValue: c.amount_value,
      slippage: c.slippage,
      jitoTip: c.jito_tip,
      copySells: c.copy_sells,
      stopLoss: c.stop_loss,
      takeProfit: c.take_profit,
      stopLossEnabled: c.stop_loss_enabled,
      takeProfitEnabled: c.take_profit_enabled,
      trailingStopLoss: c.trailing_stop_loss,
      trailingStopLossEnabled: c.trailing_stop_loss_enabled,
      securityFilterEnabled: c.security_filter_enabled,
      securityMinLiquidityUsd: c.security_min_liquidity_usd ?? 10000,
      securityBlockDangers: c.security_block_dangers ?? true,
      autoBuy: c.auto_buy ?? true,
      allowRebuy: c.allow_rebuy ?? false,
      priorityFee: c.priority_fee || 'medium',
      enabled: c.enabled,
      type: 'manual',
      status: c.enabled,
    }));
  } catch(e) {
    console.warn('Erreur chargement configs Supabase:', e.message);
    return null;
  }
}

let _isSavingConfigs = false;
let _pendingSave = null;

function saveConfigs(c, onError = null, onSavingChange = null) {
  try { localStorage.setItem(STORAGE_KEY,JSON.stringify(c)); } catch {}
  if (_isSavingConfigs) {
    _pendingSave = { c, onError, onSavingChange };
    return;
  }
  _isSavingConfigs = true;
  if (onSavingChange) onSavingChange(true);
  (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const userId = session.user.id;
      // 1. Snapshot des IDs existants avant toute modification
      const { data: existing } = await supabase.from('configs').select('id').eq('user_id', userId);
      const oldIds = (existing || []).map(r => r.id);
      // 2. Insérer d'abord — si ça échoue, les anciens restent intacts en Supabase
      let newIds = [];
      if (c.length > 0) {
        const rows = c.map(cfg => ({
          user_id: userId,
          name: cfg.name || 'Config',
          emoji: cfg.emoji || '',
          target_wallet: cfg.targetWallet || '',
          bot_wallet_id: cfg.botWalletId || '',
          amount_mode: cfg.amountMode || 'fixed',
          amount_value: String(cfg.amountValue || '0.1'),
          slippage: cfg.slippage || 'auto',
          jito_tip: cfg.jitoTip || 'low',
          copy_sells: cfg.copySells || false,
          stop_loss: String(cfg.stopLoss || '50'),
          take_profit: String(cfg.takeProfit || '200'),
          stop_loss_enabled: cfg.stopLossEnabled !== false,
          take_profit_enabled: cfg.takeProfitEnabled !== false,
          trailing_stop_loss: String(cfg.trailingStopLoss || '20'),
          trailing_stop_loss_enabled: cfg.trailingStopLossEnabled === true,
          security_filter_enabled: cfg.securityFilterEnabled !== false,
          security_min_liquidity_usd: Number(cfg.securityMinLiquidityUsd ?? 10000),
          security_block_dangers: cfg.securityBlockDangers !== false,
          auto_buy: cfg.autoBuy !== false,
          allow_rebuy: cfg.allowRebuy === true,
          priority_fee: cfg.priorityFee || 'medium',
          enabled: cfg.enabled || false,
        }));
        const { data: inserted, error: insErr } = await supabase.from('configs').insert(rows).select('id');
        if (insErr) throw new Error(insErr.message);
        newIds = (inserted || []).map(r => r.id);
      }
      // 3. Supprimer les anciennes lignes — rollback des nouvelles si ça échoue
      if (oldIds.length > 0) {
        const { error: delErr } = await supabase.from('configs').delete().in('id', oldIds);
        if (delErr) {
          if (newIds.length > 0) await supabase.from('configs').delete().in('id', newIds);
          throw new Error('Suppression anciennes configs échouée : ' + delErr.message);
        }
      }
    } catch(e) {
      const msg = 'Configs non sauvegardées en cloud — ' + e.message;
      if (onError) onError(msg); else console.warn(msg);
    } finally {
      _isSavingConfigs = false;
      if (onSavingChange) onSavingChange(false);
      if (_pendingSave) {
        const next = _pendingSave;
        _pendingSave = null;
        saveConfigs(next.c, next.onError, next.onSavingChange);
      }
    }
  })();
}

function timeAgo(closedTs) {
  const diff = Math.floor(Date.now() / 1000) - closedTs;
  if (diff < 60)    return 'à l\'instant';
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}



function Toggle({checked,onChange}) {
  return (
    <div onClick={e=>{e.stopPropagation();onChange(!checked);}} style={{width:36,height:20,borderRadius:10,background:checked?'#00e5cc':'#1a2030',border:`1px solid ${checked?'#00e5cc':'#2a3040'}`,cursor:'pointer',position:'relative',transition:'all 0.2s',flexShrink:0}}>
      <div style={{position:'absolute',top:2,left:checked?16:2,width:14,height:14,borderRadius:'50%',background:'#fff',transition:'all 0.2s'}}/>
    </div>
  );
}


function InAppAlert({alerts, onDismiss}) {
  if(!alerts||!alerts.length) return null;
  return (
    <div style={{position:'fixed',top:70,right:16,zIndex:3000,display:'flex',flexDirection:'column',gap:6,maxWidth:320 }}>
      {alerts.map((a,i)=>(
        <div key={i} onClick={()=>onDismiss(i)}
          style={{background:'rgba(11,14,20,0.97)',border:`1px solid ${a.type==='buy'?'#00e5cc33':'#ff6b6b33'}`,borderRadius:8,padding:'10px 14px',cursor:'pointer',boxShadow:'0 4px 16px rgba(0,0,0,0.6)',display:'flex',alignItems:'flex-start',gap:10,animation:'slideIn 0.3s ease'}}>
          <span style={{fontSize:16,flexShrink:0}}>{a.type==='buy'?'🟢':'🔴'}</span>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,fontWeight:700,color:a.type==='buy'?'#00e5cc':'#ff6b6b'}}>{a.type==='buy'?'Trade copié !':'Vente copiée !'}</div>
            <div style={{fontSize:10,color:'#c0c8d8',marginTop:2}}>{a.symbol} — {a.isSim?'SIM':'LIVE'}</div>
            <div style={{fontSize:9,color:'#5a6a80',marginTop:1}}>{a.detail}</div>
          </div>
          <span style={{fontSize:10,color:'#3a4a5a',flexShrink:0}}>×</span>
        </div>
      ))}
    </div>
  );
}

function ConfigModal({ onSave, onClose, initial, botWallet, walletList=[], balances={}, isSaving=false }) {
  const [form,setForm]=useState({
    name:initial?.name||'', emoji:initial?.emoji||'', targetWallet:initial?.targetWallet||'',
    autoBuy:initial?.autoBuy??true, allowRebuy:initial?.allowRebuy??false,
    amountMode:initial?.amountMode||'fixed', amountValue:initial?.amountValue||'0.1',
    priorityFee:initial?.priorityFee||'medium', jitoTip:initial?.jitoTip||'low',
    slippage:initial?.slippage||'auto',
    copySells:initial?.copySells??false, status:initial?.status??initial?.enabled??true,
    stopLoss:initial?.stopLoss||'50', takeProfit:initial?.takeProfit||'200',
    stopLossEnabled:initial?.stopLossEnabled??true,
    takeProfitEnabled:initial?.takeProfitEnabled??true,
    trailingStopLossEnabled:initial?.trailingStopLossEnabled??false,
    trailingStopLoss:initial?.trailingStopLoss||'20',
    securityFilterEnabled:initial?.securityFilterEnabled??true,
    securityMinLiquidityUsd:initial?.securityMinLiquidityUsd??10000,
    securityBlockDangers:initial?.securityBlockDangers??true,
    botWalletId:initial?.botWalletId||walletList[0]?.id||'',
  });
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const inp={background:'#0d1117',border:'1px solid #1e2a38',borderRadius:6,color:'#c0c8d8',fontSize:13,padding:'10px 14px',width:'100%',outline:'none',boxSizing:'border-box',fontFamily:'inherit'};
  const sel={...inp,appearance:'none',cursor:'pointer'};
  const lbl={fontSize:10,color:'#00e5cc',letterSpacing:'1px',marginBottom:6,display:'block',fontFamily:'monospace'};
  const row={display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',borderBottom:'1px solid #1a2030'};
  const sep=(t)=>(<div style={{display:'flex',alignItems:'center',gap:12,margin:'8px 0',color:'#3a4a5a',fontSize:11}}><div style={{flex:1,height:1,background:'#1a2030'}}/>{t}<div style={{flex:1,height:1,background:'#1a2030'}}/></div>);
  return (
    <ModalOverlay onClose={onClose} zIndex={1000}>
      <div style={{background:'#0b0e14',border:'1px solid rgba(0,229,204,0.3)',borderRadius:10,width:480,maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 18px',background:'#0d1018',borderBottom:'1px solid rgba(0,229,204,0.12)',position:'sticky',top:0,zIndex:1}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{display:'flex',gap:5}}><span style={{width:12,height:12,borderRadius:'50%',background:'#ff5f57',display:'block'}}/><span style={{width:12,height:12,borderRadius:'50%',background:'#febc2e',display:'block'}}/><span style={{width:12,height:12,borderRadius:'50%',background:'#28c840',display:'block'}}/></div>
            <span style={{fontSize:12,fontWeight:700,color:'#c0c8d8',fontFamily:'monospace',letterSpacing:1}}>{initial?'Modifier config':'Nouvelle config'}</span>
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',color:'#5a6a80',cursor:'pointer',fontSize:18}}>×</button>
        </div>
        <div style={{padding:'20px 22px',display:'flex',flexDirection:'column',gap:14}}>
          <div><span style={lbl}>&gt; NOM</span><input style={inp} placeholder="ex. Whale 1"value={form.name} onChange={e=>set('name',e.target.value)}/></div>
          <div><span style={lbl}>&gt; EMOJI</span><input style={inp} placeholder="" value={form.emoji} onChange={e=>set('emoji',e.target.value)}/></div>
          <div><span style={lbl}>&gt; WALLET À COPIER</span><input style={inp} placeholder="Adresse Solana du wallet à suivre..."value={form.targetWallet} onChange={e=>set('targetWallet',e.target.value)}/></div>
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#8a9ab0',fontFamily:'monospace'}}>&gt; COPIE AUTO LES ACHATS</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Achète automatiquement quand le wallet suivi achète</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}><Toggle checked={form.autoBuy} onChange={v=>set('autoBuy',v)}/><span style={{fontSize:12,color:form.autoBuy?'#00e5cc':'#5a6a80',fontFamily:'monospace'}}>{form.autoBuy?'OUI':'NON'}</span></div>
          </div>
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#8a9ab0',fontFamily:'monospace'}}>&gt; AUTORISER LE RE-ACHAT</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Racheter un token déjà copié</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}><Toggle checked={form.allowRebuy} onChange={v=>set('allowRebuy',v)}/><span style={{fontSize:12,color:form.allowRebuy?'#00e5cc':'#5a6a80',fontFamily:'monospace'}}>{form.allowRebuy?'OUI':'NON'}</span></div>
          </div>
          {!form.allowRebuy&&<div style={{background:'rgba(0,229,204,0.05)',border:'1px solid rgba(0,229,204,0.15)',borderRadius:6,padding:'10px 14px',fontSize:11,color:'#5a8a7a',fontStyle:'italic'}}>Un token déjà copié ne sera jamais re-copié</div>}
          <div>
            <span style={lbl}>&gt; WALLET UTILISÉ POUR LES ACHATS</span>
            <div style={{fontSize:9,color:'#3a4a5a',marginBottom:8}}>Quel wallet sera utilisé pour exécuter les trades ?</div>
            {walletList.length>0?(
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {walletList.map(w=>{
                  const bal = w.isSim ? (balances['sim']||0) : (balances[w.id]||0);
                  const isSelected=form.botWalletId===w.id;
                  return (
                    <div key={w.id} onClick={()=>set('botWalletId',w.id)}
                      style={{background:'#0d1117',border:`1px solid ${isSelected?(w.isSim?'#7c4dff44':'#00e5cc44'):'#1a2030'}`,borderRadius:6,padding:'10px 14px',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',transition:'all 0.15s'}}>
                      <div>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          {w.isSim&&<span style={{fontSize:11}}></span>}
                          <div style={{fontSize:12,fontWeight:700,color:isSelected?(w.isSim?'#7c4dff':'#00e5cc'):'#c0c8d8'}}>{w.name}</div>
                          {w.isSim&&<Badge variant="sim">SIMULATION</Badge>}
                        </div>
                        <div style={{fontSize:9,color:'#5a6a80',fontFamily:'monospace',marginTop:2}}>
                          {w.isSim?'Entraînement — aucun vrai SOL dépensé':`${w.address.slice(0,8)}...${w.address.slice(-4)}`}
                        </div>
                      </div>
                      <div style={{textAlign:'right',display:'flex',flexDirection:'column',alignItems:'flex-end',gap:3}}>
                        <span style={{fontSize:11,fontWeight:700,color:isSelected?(w.isSim?'#7c4dff':'#00e5cc'):'#8a9ab0',fontFamily:'monospace'}}>{bal.toFixed(4)} SOL</span>
                        {isSelected&&<Badge variant={w.isSim?'sim':'live'}> SÉLECTIONNÉ</Badge>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ):(
              <div style={{background:'rgba(255,107,107,0.05)',border:'1px solid #ff6b6b33',borderRadius:6,padding:'12px 14px',fontSize:12,color:'#ff6b6b'}}> Aucun wallet bot — crée-en un via  BOT</div>
            )}
          </div>
          {sep('MONTANT PAR TRADE')}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><span style={lbl}>&gt; TYPE</span><select style={sel} value={form.amountMode} onChange={e=>set('amountMode',e.target.value)}><option value="fixed">Montant fixe (SOL)</option><option value="percent">% du solde</option></select></div>
            <div><span style={lbl}>&gt; VALEUR</span><div style={{display:'flex'}}><input style={{...inp,borderRadius:'6px 0 0 6px',borderRight:'none'}} type="number"value={form.amountValue} onChange={e=>set('amountValue',e.target.value)}/><div style={{background:'#1a2030',border:'1px solid #1e2a38',borderRadius:'0 6px 6px 0',padding:'10px 12px',color:'#8a9ab0',fontSize:12,fontFamily:'monospace'}}>{form.amountMode==='percent'?'%':'SOL'}</div></div></div>
          </div>
          {sep('PARAMÈTRES AVANCÉS')}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><span style={lbl}>&gt; PRIORITÉ TX</span><select style={sel} value={form.priorityFee} onChange={e=>set('priorityFee',e.target.value)}><option value="low">Faible</option><option value="medium">Moyenne</option><option value="high">Haute</option><option value="very_high">Très haute</option></select></div>
            <div><span style={lbl}>&gt; JITO TIP</span><select style={sel} value={form.jitoTip} onChange={e=>set('jitoTip',e.target.value)}><option value="low">Faible</option><option value="medium">Moyen</option><option value="high">Haut</option></select></div>
          </div>
          <div><span style={lbl}>&gt; SLIPPAGE</span><select style={sel} value={form.slippage} onChange={e=>set('slippage',e.target.value)}><option value="auto">Auto</option><option value="1">1%</option><option value="5">5%</option><option value="10">10%</option><option value="20">20%</option></select></div>
          {sep('VENTES')}
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#8a9ab0',fontFamily:'monospace'}}>&gt; COPIER LES VENTES</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:3}}>Vend automatiquement quand le wallet suivi vend</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Toggle checked={form.copySells} onChange={v=>set('copySells',v)}/>
              <span style={{fontSize:12,color:form.copySells?'#00e5cc':'#5a6a80',fontFamily:'monospace'}}>{form.copySells?'OUI':'NON'}</span>
            </div>
          </div>
          {sep('PROTECTION')}
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#ff6b6b',fontFamily:'monospace'}}>&gt; STOP-LOSS</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Vente automatique en cas de perte</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Toggle checked={form.stopLossEnabled} onChange={v=>set('stopLossEnabled',v)}/>
              <span style={{fontSize:12,color:form.stopLossEnabled?'#ff6b6b':'#5a6a80',fontFamily:'monospace'}}>{form.stopLossEnabled?'ACTIF':'OFF'}</span>
            </div>
          </div>
          {form.stopLossEnabled&&(
            <div>
              <div style={{display:'flex'}}>
                <input style={{...inp,borderRadius:'6px 0 0 6px',borderRight:'none',color:'#ff6b6b'}} type="number"placeholder="50"value={form.stopLoss} onChange={e=>set('stopLoss',e.target.value)}/>
                <div style={{background:'#1a2030',border:'1px solid #1e2a38',borderRadius:'0 6px 6px 0',padding:'10px 12px',color:'#ff6b6b',fontSize:12}}>%</div>
              </div>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:4}}>Vend si la perte dépasse {form.stopLoss}%</div>
            </div>
          )}
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#00e5cc',fontFamily:'monospace'}}>&gt; TAKE-PROFIT</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Vente automatique pour sécuriser les gains</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Toggle checked={form.takeProfitEnabled} onChange={v=>set('takeProfitEnabled',v)}/>
              <span style={{fontSize:12,color:form.takeProfitEnabled?'#00e5cc':'#5a6a80',fontFamily:'monospace'}}>{form.takeProfitEnabled?'ACTIF':'OFF'}</span>
            </div>
          </div>
          {form.takeProfitEnabled&&(
            <div>
              <div style={{display:'flex'}}>
                <input style={{...inp,borderRadius:'6px 0 0 6px',borderRight:'none',color:'#00e5cc'}} type="number"placeholder="200"value={form.takeProfit} onChange={e=>set('takeProfit',e.target.value)}/>
                <div style={{background:'#1a2030',border:'1px solid #1e2a38',borderRadius:'0 6px 6px 0',padding:'10px 12px',color:'#00e5cc',fontSize:12}}>%</div>
              </div>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:4}}>Vend si le gain dépasse {form.takeProfit}%</div>
            </div>
          )}
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#f0a500',fontFamily:'monospace'}}>&gt; TRAILING STOP-LOSS</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Stop qui suit le prix à la hausse</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Toggle checked={form.trailingStopLossEnabled} onChange={v=>set('trailingStopLossEnabled',v)}/>
              <span style={{fontSize:12,color:form.trailingStopLossEnabled?'#f0a500':'#5a6a80',fontFamily:'monospace'}}>{form.trailingStopLossEnabled?'ACTIF':'OFF'}</span>
            </div>
          </div>
          {form.trailingStopLossEnabled&&(
            <div>
              <div style={{display:'flex'}}>
                <input style={{...inp,borderRadius:'6px 0 0 6px',borderRight:'none',color:'#f0a500'}} type="number" placeholder="20" value={form.trailingStopLoss} onChange={e=>set('trailingStopLoss',e.target.value)}/>
                <div style={{background:'#1a2030',border:'1px solid #1e2a38',borderRadius:'0 6px 6px 0',padding:'10px 12px',color:'#f0a500',fontSize:12}}>%</div>
              </div>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:4}}>Vend si le prix chute de {form.trailingStopLoss}% depuis le plus haut atteint</div>
            </div>
          )}
          <div style={row}>
            <div>
              <span style={{fontSize:12,color:'#7c4dff',fontFamily:'monospace'}}>&gt; FILTRE DE SÉCURITÉ</span>
              <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Vérifie si le token est un rug/honeypot avant d'acheter</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <Toggle checked={form.securityFilterEnabled} onChange={v=>set('securityFilterEnabled',v)}/>
              <span style={{fontSize:12,color:form.securityFilterEnabled?'#7c4dff':'#5a6a80',fontFamily:'monospace'}}>{form.securityFilterEnabled?'ACTIF':'OFF'}</span>
            </div>
          </div>
          {form.securityFilterEnabled&&(
            <div style={{display:'flex',flexDirection:'column',gap:10,background:'rgba(124,77,255,0.05)',border:'1px solid #7c4dff22',borderRadius:6,padding:'12px 14px'}}>
              <div>
                <span style={lbl}>&gt; LIQUIDITÉ MINIMUM (DEXSCREENER)</span>
                <div style={{display:'flex'}}>
                  <input style={{...inp,borderRadius:'6px 0 0 6px',borderRight:'none',color:'#7c4dff'}} type="number" placeholder="10000" value={form.securityMinLiquidityUsd} onChange={e=>set('securityMinLiquidityUsd',e.target.value)}/>
                  <div style={{background:'#1a2030',border:'1px solid #1e2a38',borderRadius:'0 6px 6px 0',padding:'10px 12px',color:'#7c4dff',fontSize:12}}>$</div>
                </div>
                <div style={{fontSize:9,color:'#3a4a5a',marginTop:4}}>Bloque si la liquidité DexScreener est inférieure à {Number(form.securityMinLiquidityUsd).toLocaleString()}$ (0 = désactivé)</div>
              </div>
              <div style={row}>
                <div>
                  <span style={{fontSize:12,color:'#7c4dff',fontFamily:'monospace'}}>&gt; BLOQUER SI DANGER DÉTECTÉ</span>
                  <div style={{fontSize:9,color:'#3a4a5a',marginTop:2}}>Bloque si RugCheck signale un risque critique, quel que soit le score</div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <Toggle checked={form.securityBlockDangers} onChange={v=>set('securityBlockDangers',v)}/>
                  <span style={{fontSize:12,color:form.securityBlockDangers?'#7c4dff':'#5a6a80',fontFamily:'monospace'}}>{form.securityBlockDangers?'OUI':'NON'}</span>
                </div>
              </div>
            </div>
          )}
          {sep('')}
          <div style={row}><span style={{fontSize:12,color:'#8a9ab0',fontFamily:'monospace'}}>&gt; STATUT</span><div style={{display:'flex',alignItems:'center',gap:10}}><Toggle checked={form.status} onChange={v=>set('status',v)}/><span style={{display:'flex',alignItems:'center',gap:5,fontSize:12,fontFamily:'monospace'}}><span style={{width:7,height:7,borderRadius:'50%',background:form.status?'#00e5cc':'#3a4a5a',display:'inline-block'}}/><span style={{color:form.status?'#00e5cc':'#5a6a80'}}>{form.status?'ACTIF':'INACTIF'}</span></span></div></div>
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:12,padding:'16px 22px',borderTop:'1px solid #1a2030',position:'sticky',bottom:0,background:'#0b0e14'}}>
          <button onClick={onClose} style={{padding:'9px 20px',borderRadius:6,background:'transparent',border:'1px solid #1e2530',color:'#5a6a80',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'monospace'}}>Annuler</button>
          <button onClick={()=>onSave(form)} disabled={isSaving} style={{padding:'9px 20px',borderRadius:6,background:isSaving?'#1a2030':'#00e5cc',border:'none',color:isSaving?'#4a6a80':'#060809',fontSize:12,fontWeight:800,cursor:isSaving?'not-allowed':'pointer',fontFamily:'monospace',transition:'all 0.2s'}}>{isSaving?'Sauvegarde…':'Enregistrer'}</button>
        </div>
      </div>
    </ModalOverlay>
  );
}function ConfigsPanel({ configs, selectedId, setSelectedId, toggleConfig, setEditConfig, setShowModal, deleteConfig, onGoToNetwork }) {
  const hasConfigs = configs.length > 0;
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div style={{padding:'12px 14px',borderBottom:'1px solid #1a2030',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <span style={{fontSize:11,fontWeight:700,color:'#5a6a80',letterSpacing:'1px'}}>MES CONFIGS</span>
        <button onClick={()=>{setEditConfig(null);setShowModal(true);}}
          style={{padding:'4px 10px',borderRadius:5,background:'rgba(0,229,204,0.1)',border:'1px solid #00e5cc33',color:'#00e5cc',fontSize:11,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
          + Nouvelle
        </button>
      </div>
      {!hasConfigs?(
        <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:24,gap:20}}>
          <div style={{fontSize:32,opacity:0.3}}></div>
          <div style={{fontSize:13,fontWeight:700,color:'#4a5a70',textAlign:'center'}}>Aucune config</div>
          <div style={{display:'flex',flexDirection:'column',gap:10,width:'100%'}}>
            {[['1','Crée une config','Clique "+ Nouvelle"en haut'],['2','Entre le wallet à copier','L\'adresse Solana du trader'],['3','Active le toggle','Le bot commence à copier']].map(([n,title,sub])=>(
              <div key={n} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',background:'#0d1117',border:'1px solid #1a2030',borderRadius:8}}>
                <span style={{width:20,height:20,borderRadius:'50%',background:'rgba(0,229,204,0.1)',border:'1px solid #00e5cc33',color:'#00e5cc',fontSize:10,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{n}</span>
                <div><div style={{fontSize:11,fontWeight:700,color:'#c0c8d8'}}>{title}</div><div style={{fontSize:10,color:'#3a4a5a',marginTop:2}}>{sub}</div></div>
              </div>
            ))}
          </div>
          <button onClick={()=>setShowModal(true)} style={{width:'100%',padding:'12px',borderRadius:8,background:'linear-gradient(135deg,#00e5cc,#00b8a0)',border:'none',color:'#060809',fontSize:13,fontWeight:800,cursor:'pointer'}}>+ Créer ma première config</button>
        </div>
      ):(
        <div style={{overflowY:'auto',flex:1}}>
          {configs.map(c=>{
            const isSelected = selectedId===c.id;
            return (
              <div key={c.id} onClick={()=>setSelectedId(c.id===selectedId?null:c.id)}
                style={{borderBottom:'1px solid #0f1520',cursor:'pointer',background:isSelected?'rgba(0,229,204,0.04)':'transparent',borderLeft:`2px solid ${isSelected?'#00e5cc':'transparent'}`}}>
                <div style={{padding:'10px 14px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                    <span style={{width:6,height:6,borderRadius:'50%',background:c.enabled?'#00e5cc':'#2a3a4a',flexShrink:0}}/>
                    <span style={{fontSize:12,fontWeight:700,color:'#c0c8d8',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.emoji||''} {c.name}</span>
                    {c.botWalletId&&<Badge variant={c.botWalletId==='sim'?'sim':'live'}>{c.botWalletId==='sim'?'SIM':'LIVE'}</Badge>}
                    <div onClick={e=>e.stopPropagation()} style={{flexShrink:0}}><Toggle checked={c.enabled} onChange={()=>toggleConfig(c.id)}/></div>
                  </div>
                  <div style={{fontSize:10,color:'#3a4a5a',fontFamily:'monospace',paddingLeft:12,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {c.targetWallet?`${c.targetWallet.slice(0,12)}...${c.targetWallet.slice(-4)}`:'Aucun wallet configuré'}
                  </div>
                  {c.enabled&&c.targetWallet&&c.targetWallet.length>10&&(
                    <div style={{display:'flex',alignItems:'center',gap:4,marginTop:4,paddingLeft:12}}>
                      <LiveDot color="accent" size={5} />
                      <span style={{fontSize:10,color:'#00e5cc'}}>En écoute</span>
                    </div>
                  )}
                  <div style={{display:'flex',gap:6,paddingLeft:12,marginTop:3,flexWrap:'wrap'}}>
                    {c.amountValue&&<span style={{fontSize:9,color:'#5a6a80'}}> {c.amountValue}{c.amountMode!=='fixed'?'%':'SOL'}</span>}
                    {c.stopLossEnabled!==false&&c.stopLoss&&<span style={{fontSize:9,color:'#ff6b6b'}}> SL -{c.stopLoss}%</span>}
                    {c.takeProfitEnabled!==false&&c.takeProfit&&<span style={{fontSize:9,color:'#00e5cc'}}>🟢 TP +{c.takeProfit}%</span>}
                  </div>
                </div>
                {isSelected&&(
                  <div style={{display:'flex',gap:6,padding:'0 14px 10px',paddingLeft:26}}>
                    <button onClick={e=>{e.stopPropagation();setEditConfig(c);setShowModal(true);}} style={{flex:1,padding:'6px',borderRadius:5,background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc22',color:'#00e5cc',fontSize:10,fontWeight:700,cursor:'pointer'}}> Modifier</button>
                    <button onClick={e=>{e.stopPropagation();if(window.confirm('Supprimer cette config ?'))deleteConfig(c.id);}} style={{padding:'6px 10px',borderRadius:5,background:'rgba(255,107,107,0.08)',border:'1px solid #ff6b6b22',color:'#ff6b6b',fontSize:10,cursor:'pointer'}}></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


const tokenMetaCache = {};

function PositionsPanel({ botStats, selectedPosition, onSelectPosition, allTrades, configs, ghostMints, onLivePnl }) {
  const { isImpersonating } = useEffectiveUser();
  const [monTab,setMonTab]=useState('ouvertes');
  const [histFilter,setHistFilter]=useState('all');
  const [closingAll,setClosingAll]=useState(false);
  const [refreshKey,setRefreshKey]=useState(0);
  const [dismissedGhosts,setDismissedGhosts]=useState(new Set());
  const [livePrices,setLivePrices]=useState({});
  const [tokenMetas,setTokenMetas]=useState({});
  const fetchingPricesRef=useRef(false);
  const mintPriceCacheRef=useRef({});
  const positions=botStats.positions||[];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const localTrades=useMemo(()=>JSON.parse(localStorage.getItem('bot-trades')||'[]'),[refreshKey]);
  const trades = allTrades && allTrades.length > 0 ? allTrades : localTrades;

  const closePosition = async (p) => {
    if (isImpersonating) return;
    // bot-trades
    const trades2 = JSON.parse(localStorage.getItem('bot-trades')||'[]').filter(t => {
      if (!t.isSim || t.resultat !== 'En cours' || t.mint !== p.mint) return true;
      if (p.txHash && t.txHash) return t.txHash !== p.txHash;
      return false;
    });
    localStorage.setItem('bot-trades', JSON.stringify(trades2));
    // bot-stats
    const stats = JSON.parse(localStorage.getItem('bot-stats')||'{}');
    if (stats.positions) { stats.positions = stats.positions.filter(pos => !(pos.mint===p.mint && pos.walletIsSim)); localStorage.setItem('bot-stats', JSON.stringify(stats)); }
    // Supabase
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        let q = supabase.from('trades').delete().eq('user_id', session.user.id).eq('is_sim', true).eq('resultat', 'En cours');
        if (p.id) q = q.eq('id', p.id); else q = q.eq('mint', p.mint);
        await q;
      }
    } catch(e) { console.warn('[copytrade] closePosition Supabase:', e.message); }
    setRefreshKey(k => k+1);
  };

  const closeAllSim = async () => {
    if (isImpersonating) return;
    setClosingAll(true);
    // Retirer uniquement les entrées SIM — préserver les trades LIVE
    const allStoredTrades = JSON.parse(localStorage.getItem('bot-trades') || '[]');
    const liveOnly = allStoredTrades.filter(t => !t.isSim);
    if (liveOnly.length > 0) localStorage.setItem('bot-trades', JSON.stringify(liveOnly));
    else localStorage.removeItem('bot-trades');
    const storedStats = JSON.parse(localStorage.getItem('bot-stats') || '{}');
    if (storedStats.positions) {
      storedStats.positions = storedStats.positions.filter(p => !p.walletIsSim);
      localStorage.setItem('bot-stats', JSON.stringify(storedStats));
    }
    // Supabase
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) await supabase.from('trades').delete().eq('user_id', session.user.id).eq('resultat', 'En cours').eq('is_sim', true);
    } catch(e) { console.warn('[copytrade] closeAllSim Supabase:', e.message); }
    setRefreshKey(k => k+1);
    setClosingAll(false);
  };

  const closeGhostPosition = async (p) => {
    if (isImpersonating) return;
    if (!window.confirm('Archiver cette position désynchronisée dans l\'historique ?')) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      await supabase.from('trades')
        .update({ resultat: 'Fermé (historique)', pnl_usd: null, vente_usd: null, roi: null, sortie_ts: Math.floor(Date.now() / 1000), close_reason: 'MANUAL' })
        .eq('user_id', session.user.id).eq('mint', p.mint).eq('resultat', 'En cours').eq('is_sim', false);
      setDismissedGhosts(prev => new Set([...prev, p.mint + ':' + (p.botWalletId || '')]));
    } catch(e) { console.warn('[copytrade] closeGhostPosition:', e.message); }
  };

  const closed=trades.filter(t=>t.resultat&&t.resultat!=='En cours');
  const closedFiltered = histFilter==='sim'?closed.filter(t=>t.isSim):histFilter==='live'?closed.filter(t=>!t.isSim):closed;

  const rawSimTrades = trades.filter(t=>t.resultat==='En cours'&&t.isSim);
  const simMints = allTrades&&allTrades.length>0 ? new Set() : new Set(positions.filter(p=>p.walletIsSim).map(p=>p.mint));
  const instantSimPositions = rawSimTrades
    .filter(t=>!simMints.has(t.mint))
    .map(t=>{
      const matchSimCfg=configs?.find(c=>c.targetWallet===t.walletSuivi);
      return {
        mint:t.mint, symbol:t.symbol||'?', tokenAmount:t.tokenAmount||0,
        achatUsd:t.achatUsd||0, currentUsd:t.achatUsd||0, pnlUsd:0, pnlPct:0,
        prixEntree:t.prixEntree||(t.tokenAmount>0&&t.achatUsd>0?t.achatUsd/t.tokenAmount:0),
        txHash:t.txHash, id:t.id,
        botWalletId:t.botWalletId, walletName:'Sim Wallet', walletIsSim:true,
        configName:matchSimCfg?.name||null,
      };
    });
  const rawLiveTrades = trades.filter(t=>t.resultat==='En cours'&&!t.isSim);
  const liveMints = allTrades&&allTrades.length>0 ? new Set() : new Set(positions.filter(p=>!p.walletIsSim).map(p=>p.mint));
  const instantLivePositions = rawLiveTrades
    .filter(t=>!liveMints.has(t.mint))
    .map(t=>{
      const matchCfg=configs?.find(c=>c.targetWallet===t.walletSuivi);
      return {
        mint:t.mint, symbol:t.symbol||'?', tokenAmount:t.tokenAmount||0,
        achatUsd:t.achatUsd||0, currentUsd:t.achatUsd||0, pnlUsd:0, pnlPct:0,
        prixEntree:t.prixEntree||(t.tokenAmount>0&&t.achatUsd>0?t.achatUsd/t.tokenAmount:0), entreeTs:t.entreeTs,
        txHash:t.txHash, id:t.id,
        botWalletId:matchCfg?.botWalletId||null, walletName:'Live', walletIsSim:false,
        configName:matchCfg?.name||null,
      };
    });
  const allPositions=allTrades&&allTrades.length>0
    ?[...instantSimPositions,...instantLivePositions]
    :[...positions,...instantSimPositions,...instantLivePositions];
  const simPositions=allPositions.filter(p=>p.walletIsSim);
  const livePositions=allPositions.filter(p=>!p.walletIsSim);

  useEffect(()=>{
    const allOpen=[...instantLivePositions,...instantSimPositions].filter(p=>p.mint);
    if(!allOpen.length) return;
    let cancelled=false;
    const fetchPrices=async()=>{
      if(fetchingPricesRef.current) return;
      fetchingPricesRef.current=true;
      const positions=[...instantLivePositions,...instantSimPositions].filter(p=>p.mint);
      if(!positions.length){ fetchingPricesRef.current=false; return; }
      try{
        const uniqueMints=[...new Set(positions.map(p=>p.mint))];
        const ids=uniqueMints.join(',');
        const priceMap={};
        try{
          const r=await fetch('https://price.jup.ag/v6/price?ids='+ids);
          const j=await r.json();
          for(const mint of uniqueMints){
            const data=j?.data?.[mint];
            if(data?.price){
              const price=parseFloat(data.price);
              priceMap[mint]={price,symbol:data.mintSymbol};
              mintPriceCacheRef.current[mint]=price;
            }
          }
        }catch{}
        const missingMints=uniqueMints.filter(m=>!priceMap[m]);
        if(missingMints.length){
          try{
            const dr=await fetch(`https://api.dexscreener.com/latest/dex/tokens/${missingMints.join(',')}`);
            const dj=await dr.json();
            const byMint={};
            (dj?.pairs||[]).forEach(pair=>{
              const m=pair.baseToken?.address;
              if(m&&!byMint[m]&&parseFloat(pair.priceUsd||0)>0)
                byMint[m]={price:parseFloat(pair.priceUsd),symbol:pair.baseToken?.symbol};
            });
            for(const m of missingMints){
              if(byMint[m]){ priceMap[m]=byMint[m]; mintPriceCacheRef.current[m]=byMint[m].price; }
            }
          }catch{}
        }
        const newPrices={};
        for(const pos of positions){
          const pd=priceMap[pos.mint];
          if(!pd) continue;
          const key=pos.txHash||pos.mint;
          if(pos.prixEntree>0){
            const pnlPct=((pd.price-pos.prixEntree)/pos.prixEntree)*100;
            const pnlUsd=(pos.achatUsd||0)*(pnlPct/100);
            newPrices[key]={pnlPct,pnlUsd,symbol:pd.symbol||pos.symbol};
          } else {
            newPrices[key]={pnlPct:0,pnlUsd:0,symbol:pd.symbol||pos.symbol};
          }
        }
        setLivePrices(prev=>({...prev,...newPrices}));
        if(onLivePnl){
          const merged={...livePrices,...newPrices};
          const totalPnlUsd=positions.reduce((s,pos)=>s+(merged[pos.txHash||pos.mint]?.pnlUsd??0),0);
          const totalInvested=positions.reduce((s,pos)=>s+(pos.achatUsd||0),0);
          onLivePnl({pnlUsd:totalPnlUsd,pnlPct:totalInvested>0?(totalPnlUsd/totalInvested)*100:0});
        }
      }catch{}finally{ if(!cancelled) fetchingPricesRef.current=false; }
    };
    fetchPrices();
    const iv=setInterval(fetchPrices,500);
    return()=>{ cancelled=true; fetchingPricesRef.current=false; clearInterval(iv); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[instantLivePositions.length,instantSimPositions.length]);

  useEffect(()=>{
    const missingMints=[...new Set(allPositions.map(p=>p.mint))].filter(m=>m&&!(m in tokenMetaCache));
    if(!missingMints.length) return;
    missingMints.forEach(m=>{ tokenMetaCache[m]=null; });
    const batches=[];
    for(let i=0;i<missingMints.length;i+=30) batches.push(missingMints.slice(i,i+30));
    let cancelled=false;
    Promise.allSettled(batches.map(async batch=>{
      const r=await fetch(`https://api.dexscreener.com/latest/dex/tokens/${batch.join(',')}`);
      const j=await r.json();
      const updates={};
      (j.pairs||[]).forEach(pair=>{
        const mint=pair.baseToken?.address;
        if(mint&&!updates[mint]) updates[mint]={symbol:pair.baseToken?.symbol||null,logoUrl:pair.info?.imageUrl||null};
      });
      return updates;
    })).then(results=>{
      if(cancelled) return;
      const allUpdates={};
      results.forEach(r=>{ if(r.status==='fulfilled') Object.assign(allUpdates,r.value); });
      missingMints.forEach(m=>{ if(!(m in allUpdates)) allUpdates[m]={}; });
      Object.assign(tokenMetaCache,allUpdates);
      setTokenMetas(prev=>({...prev,...allUpdates}));
    });
    return()=>{ cancelled=true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[allPositions.length]);

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div style={{padding:'12px 14px',borderBottom:'1px solid #1a2030'}}><span style={{fontSize:11,fontWeight:700,color:'#5a6a80',letterSpacing:'1px'}}>MES POSITIONS</span></div>
      <div style={{display:'flex',borderBottom:'1px solid #1a2030'}}>
        <button onClick={()=>setMonTab('ouvertes')} style={{flex:1,padding:'8px',fontSize:10,fontWeight:700,cursor:'pointer',background:'transparent',border:'none',borderBottom:`2px solid ${monTab==='ouvertes'?'#00e5cc':'transparent'}`,color:monTab==='ouvertes'?'#00e5cc':'#4a5a70'}}>
          LIVE <span style={{fontSize:9,background:monTab==='ouvertes'?'#00e5cc':'#1a2030',color:monTab==='ouvertes'?'#060809':'#5a6a80',borderRadius:8,padding:'1px 5px',marginLeft:2}}>{livePositions.length}</span>
        </button>
        <button onClick={()=>setMonTab('sim')} style={{flex:1,padding:'8px',fontSize:10,fontWeight:700,cursor:'pointer',background:'transparent',border:'none',borderBottom:`2px solid ${monTab==='sim'?'#7c4dff':'transparent'}`,color:monTab==='sim'?'#7c4dff':'#4a5a70'}}>
          SIM <span style={{fontSize:9,background:monTab==='sim'?'#7c4dff':'#1a2030',color:monTab==='sim'?'#fff':'#5a6a80',borderRadius:8,padding:'1px 5px',marginLeft:2}}>{simPositions.length}</span>
        </button>
        <button onClick={()=>setMonTab('fermees')} style={{flex:1,padding:'8px',fontSize:10,fontWeight:700,cursor:'pointer',background:'transparent',border:'none',borderBottom:`2px solid ${monTab==='fermees'?'#00e5cc':'transparent'}`,color:monTab==='fermees'?'#00e5cc':'#4a5a70'}}>
          HIST. <span style={{fontSize:9,background:monTab==='fermees'?'#00e5cc':'#1a2030',color:monTab==='fermees'?'#060809':'#5a6a80',borderRadius:8,padding:'1px 5px',marginLeft:2}}>{closed.length}</span>
        </button>
      </div>
      <div style={{flex:1,overflowY:'auto'}}>
        {monTab==='ouvertes'&&(livePositions.length===0?(
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',gap:8,padding:20}}>
            <div style={{fontSize:28,opacity:0.3}}></div>
            <div style={{fontSize:12,textAlign:'center',color:'#3a4a5a'}}>Aucune position live</div>
          </div>
        ):livePositions.filter(p=>!dismissedGhosts.has(p.mint+':'+(p.botWalletId||''))).map((p,i)=>(<PositionItem key={p.txHash||i} p={p} selectedPosition={selectedPosition} onSelectPosition={onSelectPosition} livePrice={livePrices[p.txHash||p.mint]||(()=>{const mp=mintPriceCacheRef.current[p.mint];if(!mp||!(p.prixEntree>0))return undefined;const r=((mp-p.prixEntree)/p.prixEntree)*100;return{pnlPct:r,pnlUsd:(p.achatUsd||0)*(r/100),symbol:p.symbol};})()} tokenMeta={tokenMetas[p.mint]} isGhost={ghostMints?.has(p.mint+':'+p.botWalletId)||false} onCloseGhost={closeGhostPosition}/>)))}
        {monTab==='sim'&&(simPositions.length===0?(
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',gap:8,padding:20}}>
            <div style={{fontSize:28,opacity:0.3}}></div>
            <div style={{fontSize:12,textAlign:'center',color:'#3a4a5a'}}>Aucune position simulée</div>
          </div>
        ):(<>
          {simPositions.length>1&&<div style={{padding:'6px 10px',borderBottom:'1px solid #1a2030',display:'flex',justifyContent:'flex-end'}}>
            <button onClick={closeAllSim} disabled={closingAll} style={{padding:'3px 10px',borderRadius:4,fontSize:9,fontWeight:700,cursor:'pointer',background:'rgba(255,107,107,0.1)',border:'1px solid #ff6b6b33',color:'#ff6b6b'}}>
              {closingAll?'...':'✕ Tout fermer'}
            </button>
          </div>}
          {simPositions.map((p,i)=>(<PositionItem key={p.txHash||i} p={p} selectedPosition={selectedPosition} onSelectPosition={onSelectPosition} onClosePosition={closePosition} livePrice={livePrices[p.txHash||p.mint]||(()=>{const mp=mintPriceCacheRef.current[p.mint];if(!mp||!(p.prixEntree>0))return undefined;const r=((mp-p.prixEntree)/p.prixEntree)*100;return{pnlPct:r,pnlUsd:(p.achatUsd||0)*(r/100),symbol:p.symbol};})()} tokenMeta={tokenMetas[p.mint]} isGhost={ghostMints?.has(p.mint+':'+p.botWalletId)||false}/>))}
        </>))}
        {monTab==='fermees'&&(<>
          <div style={{padding:'6px 10px',borderBottom:'1px solid #1a2030',display:'flex',gap:4}}>
            {[['all','Tous'],['live','Live'],['sim','Sim']].map(([v,l])=>(
              <button key={v} onClick={()=>setHistFilter(v)}
                style={{padding:'3px 8px',borderRadius:4,fontSize:9,fontWeight:700,cursor:'pointer',background:histFilter===v?'rgba(0,229,204,0.15)':'#0d1117',border:`1px solid ${histFilter===v?'#00e5cc44':'#1a2030'}`,color:histFilter===v?'#00e5cc':'#5a6a80'}}>
                {l}
              </button>
            ))}
            <span style={{marginLeft:'auto',fontSize:9,color:'#3a4a5a',alignSelf:'center'}}>{closedFiltered.length} trades</span>
          </div>
          {closedFiltered.length===0?(
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'80%',color:'#3a4a5a',gap:8,padding:20}}>
              <div style={{fontSize:28,opacity:0.3}}></div>
              <div style={{fontSize:12}}>Aucun trade fermé</div>
            </div>
          ):closedFiltered.map((t,i)=>{
            const isHist = t.resultat==='Fermé (historique)';
            const cfgName = configs?.find(c => c.targetWallet === t.walletSuivi)?.name;
            return (
              <div key={i} onClick={()=>onSelectPosition({...t,walletIsSim:t.isSim||false})}
                style={{padding:'10px 14px',borderBottom:'1px solid #0f1520',borderLeft:`3px solid ${isHist?'#5a6a80':(t.pnlUsd||0)>=0?'#00e5cc':'#ff6b6b'}`,cursor:'pointer'}}
                onMouseEnter={e=>e.currentTarget.style.background='#0d1018'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                  <div style={{width:20,height:20,borderRadius:4,background:isHist?'rgba(90,106,128,0.1)':t.isSim?'rgba(124,77,255,0.1)':'rgba(0,229,204,0.1)',border:`1px solid ${isHist?'#5a6a8022':t.isSim?'#7c4dff22':'#00e5cc22'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:800,color:isHist?'#5a6a80':t.isSim?'#7c4dff':'#00e5cc'}}>{t.symbol?.charAt(0)||'?'}</div>
                  <span style={{fontSize:12,fontWeight:700,color:'#c0c8d8',flex:1}}>{t.symbol}</span>
                  {isHist
                    ?<Badge variant="warning">Historique</Badge>
                    :<Badge variant={t.isSim?'sim':'live'}>{t.isSim?'':''}</Badge>
                  }
                  {!isHist && t.closeReason && (
                    <span title={t.closeReason} style={{fontSize:11}}>
                      {t.closeReason==='STOP-LOSS'?'🛑':t.closeReason==='TAKE-PROFIT'?'🎯':t.closeReason==='TRAILING-STOP'?'📉':t.closeReason==='MANUAL'?'✋':t.closeReason==='PHANTOM-RECONCILE'?'👻':null}
                    </span>
                  )}
                  <span style={{fontSize:12,fontWeight:800,color:isHist?'#5a6a80':(t.pnlUsd||0)>=0?'#00e5cc':'#ff6b6b'}}>{isHist?'—':`${(t.pnlUsd||0)>=0?'+':''}${t.roi?.toFixed(1)}%`}</span>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'#3a4a5a',paddingLeft:26}}>
                  <span style={{color:isHist?'#5a6a80':(t.pnlUsd||0)>=0?'#00e5cc':'#ff6b6b',fontWeight:700}}>{isHist?'—':`${(t.pnlUsd||0)>=0?'+':''}${(t.pnlUsd||0).toFixed(2)}$`}</span>
                  <span style={{color:'#5a6a80'}}>{cfgName&&<span style={{color:'#4a5a70',marginRight:4}}>{cfgName} ·</span>}investi {(t.achatUsd||0).toFixed(2)}$</span>
                  {t.sortieTs&&<span> {timeAgo(t.sortieTs)}</span>}
                </div>
              </div>
            );
          })}
        </>)}
      </div>
    </div>
  );
}

function PositionItem({ p, selectedPosition, onSelectPosition, onClosePosition, onCloseGhost, livePrice, tokenMeta, isGhost }) {
  const isSelected = selectedPosition?.mint===p.mint
    && selectedPosition?.botWalletId===p.botWalletId
    && (!selectedPosition?.txHash||!p.txHash||selectedPosition.txHash===p.txHash);
  const pnlPct = livePrice?.pnlPct ?? p.pnlPct ?? 0;
  const pnlUsd = livePrice?.pnlUsd ?? p.pnlUsd ?? 0;
  const symbol = livePrice?.symbol || tokenMeta?.symbol || p.symbol;
  const pnlColor = pnlPct>=0?'#00e5cc':'#ff6b6b';
  const barPct = Math.min(100, Math.max(0, 50 + pnlPct / 2));
  const isLive = !!livePrice;
  const logoUrl = tokenMeta?.logoUrl || null;
  const [imgError, setImgError] = useState(false);
  const [hovered, setHovered] = useState(false);
  useEffect(()=>setImgError(false), [logoUrl]);
  return (
    <div onClick={()=>onSelectPosition({...p, pnlPct, pnlUsd, symbol, logoUrl, isGhost})}
      style={{padding:'10px 14px',borderBottom:'1px solid #0f1520',cursor:'pointer',borderLeft:`3px solid ${isGhost?'#f59e0b':pnlColor}`,background:isSelected?'rgba(0,229,204,0.04)':'transparent',position:'relative'}}
      onMouseEnter={e=>{e.currentTarget.style.background='#0d1018';setHovered(true);}}
      onMouseLeave={e=>{e.currentTarget.style.background=isSelected?'rgba(0,229,204,0.04)':'transparent';setHovered(false);}}>

      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
        <div style={{width:22,height:22,borderRadius:4,background:p.walletIsSim?'rgba(124,77,255,0.1)':'rgba(0,229,204,0.1)',border:`1px solid ${p.walletIsSim?'#7c4dff22':'#00e5cc22'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:800,color:p.walletIsSim?'#7c4dff':'#00e5cc',overflow:'hidden'}}>
          {logoUrl&&!imgError?<img src={logoUrl} alt="" width={22} height={22} style={{width:'100%',height:'100%',objectFit:'cover'}} onError={()=>setImgError(true)}/>:(symbol?.charAt(0)||'?')}
        </div>
        <span style={{fontSize:12,fontWeight:700,color:isGhost?'#9ca3af':'#c0c8d8',flex:1}}>{symbol}</span>
        {isGhost&&<Badge variant="warning">⚠ Désync.</Badge>}
        {!isGhost&&p.walletName&&<Badge variant={p.walletIsSim?'sim':'live'}>{p.walletIsSim?'SIM':'LIVE'}</Badge>}
        <span style={{fontSize:11,fontWeight:800,color:isGhost?'#6b7280':pnlColor}}>{pnlPct>=0?'+':''}{pnlPct.toFixed(1)}%</span>
        {isLive&&!isGhost&&<LiveDot color="accent" size={5} />}
        {p.walletIsSim && onClosePosition && (
          <button onClick={e=>{e.stopPropagation();onClosePosition(p);}}
            title="Fermer la position"
            style={{width:16,height:16,borderRadius:3,background:'rgba(255,107,107,0.15)',border:'1px solid #ff6b6b33',color:'#ff6b6b',fontSize:10,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,padding:0,lineHeight:1}}>✕</button>
        )}
        {isGhost && onCloseGhost && hovered && (
          <button onClick={e=>{e.stopPropagation();onCloseGhost(p);}}
            title="Archiver dans l'historique"
            style={{width:16,height:16,borderRadius:3,background:'rgba(245,158,11,0.15)',border:'1px solid rgba(245,158,11,0.4)',color:'#f59e0b',fontSize:10,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,padding:0,lineHeight:1}}>✕</button>
        )}
      </div>
      <div style={{height:3,background:'#1a2030',borderRadius:2,marginBottom:4,overflow:'hidden'}}>
        <div style={{height:'100%',width:`${barPct}%`,background:pnlColor,borderRadius:2,transition:'width 0.3s'}}/>
      </div>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:10,paddingLeft:28}}>
        <span style={{color:'#3a4a5a'}}>
          {p.configName&&<span style={{color:'#4a5a70',marginRight:4}}>{p.configName} ·</span>}
          {p.achatUsd?.toFixed(2)}$ investi
        </span>
        <span style={{color:pnlColor,fontWeight:700}}>{pnlUsd>=0?'+':''}{pnlUsd.toFixed(2)}$</span>
      </div>
    </div>
  );
}

function AnalyticsPanel({ trades }) {
  const [filter,setFilter]=useState('all');
  const filtered = filter==='sim'?trades.filter(t=>t.isSim):filter==='live'?trades.filter(t=>!t.isSim):trades;
  const stats = calcAnalytics(filtered);

  if(!stats) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',gap:12,color:'#3a4a5a',padding:20}}>
      <div style={{fontSize:32,opacity:0.2}}></div>
      <div style={{fontSize:13,fontWeight:600,color:'#3a4a5a',textAlign:'center'}}>Pas encore de trades fermés</div>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',overflowY:'auto'}}>
      <div style={{padding:'12px 14px',borderBottom:'1px solid #1a2030',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <span style={{fontSize:11,fontWeight:700,color:'#5a6a80',letterSpacing:'1px'}}> ANALYTICS</span>
        <div style={{display:'flex',gap:4}}>
          {[['all','Tous'],['live',''],['sim','']].map(([v,l])=>(
            <button key={v} onClick={()=>setFilter(v)}
              style={{padding:'3px 8px',borderRadius:4,fontSize:9,fontWeight:700,cursor:'pointer',background:filter===v?'rgba(0,229,204,0.15)':'#0d1117',border:`1px solid ${filter===v?'#00e5cc44':'#1a2030'}`,color:filter===v?'#00e5cc':'#5a6a80'}}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div style={{padding:'14px',display:'flex',flexDirection:'column',gap:12}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          <StatCard label="WIN RATE"  value={`${stats.winRate}%`}                                               trend={stats.winRate>=50?'accent':'down'}     variant="mini" />
          <StatCard label="TRADES"    value={`${stats.total}`}                                                   trend="neutral"                               variant="mini" />
          <StatCard label="PNL TOTAL" value={`${stats.totalPnl>=0?'+':''}${stats.totalPnl.toFixed(2)}$`}        trend={stats.totalPnl>=0?'accent':'down'}      variant="mini" />
          <StatCard label="ROI"       value={`${stats.totalPnlPct>=0?'+':''}${stats.totalPnlPct.toFixed(1)}%`}  trend={stats.totalPnlPct>=0?'accent':'down'}   variant="mini" />
        </div>
        <div style={{background:'#0d1117',border:'1px solid #1a2030',borderRadius:8,padding:'12px'}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:10}}>
            <span style={{color:'#00e5cc'}}> {stats.winners} gagnants</span>
            <span style={{color:'#ff6b6b'}}> {stats.losers} perdants</span>
          </div>
          <div style={{height:6,background:'#1a2030',borderRadius:3,overflow:'hidden'}}>
            <div style={{height:'100%',width:`${stats.winRate}%`,background:'linear-gradient(90deg,#00e5cc,#00b8a0)',borderRadius:3}}/>
          </div>
        </div>
        {stats.bestTrade&&(
          <div style={{background:'#0d1117',border:'1px solid #1a2030',borderRadius:8,padding:'12px'}}>
            <div style={{fontSize:9,color:'#3a4a5a',marginBottom:8,letterSpacing:'0.8px'}}>MEILLEUR / PIRE TRADE</div>
            <div style={{display:'flex',gap:8}}>
              <div style={{flex:1,background:'rgba(0,229,204,0.05)',border:'1px solid #00e5cc22',borderRadius:6,padding:'8px 10px'}}>
                <div style={{fontSize:9,color:'#3a4a5a',marginBottom:3}}> MEILLEUR</div>
                <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8'}}>{stats.bestTrade.symbol}</div>
                <div style={{fontSize:13,fontWeight:800,color:'#00e5cc'}}>+{stats.bestTrade.roi?.toFixed(1)}%</div>
              </div>
              <div style={{flex:1,background:'rgba(255,107,107,0.05)',border:'1px solid #ff6b6b22',borderRadius:6,padding:'8px 10px'}}>
                <div style={{fontSize:9,color:'#3a4a5a',marginBottom:3}}> PIRE</div>
                <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8'}}>{stats.worstTrade.symbol}</div>
                <div style={{fontSize:13,fontWeight:800,color:'#ff6b6b'}}>{stats.worstTrade.roi?.toFixed(1)}%</div>
              </div>
            </div>
          </div>
        )}
        <div style={{background:'#0d1117',border:'1px solid #1a2030',borderRadius:8,padding:'12px'}}>
          <div style={{fontSize:9,color:'#3a4a5a',marginBottom:6,letterSpacing:'0.8px'}}>SIM vs LIVE</div>
          <div style={{display:'flex',gap:8}}>
            <div style={{flex:1,textAlign:'center'}}>
              <div style={{fontSize:9,color:'#7c4dff',marginBottom:4}}> SIM</div>
              <div style={{fontSize:14,fontWeight:800,color:stats.simPnl>=0?'#00e5cc':'#ff6b6b',fontFamily:'monospace'}}>{stats.simPnl>=0?'+':''}{stats.simPnl.toFixed(2)}$</div>
              <div style={{fontSize:9,color:'#3a4a5a'}}>{stats.simCount} trades</div>
            </div>
            <div style={{width:1,background:'#1a2030'}}/>
            <div style={{flex:1,textAlign:'center'}}>
              <div style={{fontSize:9,color:'#00e5cc',marginBottom:4}}> LIVE</div>
              <div style={{fontSize:14,fontWeight:800,color:stats.livePnl>=0?'#00e5cc':'#ff6b6b',fontFamily:'monospace'}}>{stats.livePnl>=0?'+':''}{stats.livePnl.toFixed(2)}$</div>
              <div style={{fontSize:9,color:'#3a4a5a'}}>{stats.liveCount} trades</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PaperTradingControl() {
  const [newBal,setNewBal]=useState('');
  const [msg,setMsg]=useState('');
  const currentBal=parseFloat(localStorage.getItem('sim-wallet-balance')||'10');

  const handleReset=()=>{
    const bal=parseFloat(newBal)||10;
    localStorage.setItem('sim-wallet-balance',String(bal));
    setMsg(`Solde sim réinitialisé à ${bal} SOL`);
    setTimeout(()=>setMsg(''),3000);
    setNewBal('');
  };

  const handleClearTrades=async()=>{
    if(!window.confirm('Supprimer tout l\'historique sim ? Cette action est irréversible.')) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { error } = await supabase.from('trades').delete()
        .eq('user_id', session.user.id).eq('is_sim', true);
      if (error) {
        setMsg('Erreur suppression Supabase : ' + error.message);
        setTimeout(()=>setMsg(''),5000);
        return;
      }
    }
    const trades=JSON.parse(localStorage.getItem('bot-trades')||'[]');
    const liveTrades=trades.filter(t=>!t.isSim);
    localStorage.setItem('bot-trades',JSON.stringify(liveTrades));
    localStorage.removeItem('sim-bot-trades');
    setMsg('Historique sim effacé');
    setTimeout(()=>setMsg(''),3000);
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:8}}>
      <div style={{fontSize:12,color:'#c0c8d8',fontFamily:'monospace',fontWeight:700}}>Solde actuel: {currentBal.toFixed(4)} SOL</div>
      <div style={{display:'flex',gap:6}}>
        <input type="number"placeholder="Nouveau solde (SOL)"value={newBal} onChange={e=>setNewBal(e.target.value)}
          style={{flex:1,background:'#0d1117',border:'1px solid #1a2030',borderRadius:5,color:'#c0c8d8',fontSize:11,padding:'6px 8px',outline:'none'}}/>
        <button onClick={handleReset} style={{padding:'6px 12px',borderRadius:5,background:'rgba(124,77,255,0.2)',border:'1px solid #7c4dff33',color:'#7c4dff',fontSize:10,fontWeight:700,cursor:'pointer'}}>Reset</button>
      </div>
      <button onClick={handleClearTrades} style={{padding:'6px',borderRadius:5,background:'rgba(255,107,107,0.08)',border:'1px solid #ff6b6b22',color:'#ff6b6b',fontSize:10,fontWeight:700,cursor:'pointer'}}> Effacer historique sim</button>
      {msg&&<div style={{fontSize:10,color:'#00e5cc'}}>{msg}</div>}
    </div>
  );
}function MonitorCenterPanel({ selectedPosition, keypairs, configs, ghostMints, onSellSuccess }) {
  const [amount,setAmount]=useState('0.1'); const [sellPct,setSellPct]=useState('100%');
  const [slippage,setSlippage]=useState('auto'); const [jitoTip,setJitoTip]=useState('low');
  const [showPopup,setShowPopup]=useState(true);
  const [chartReady,setChartReady]=useState(false);
  const [tradeTab,setTradeTab]=useState('trades');
  const [popupPos,setPopupPos]=useState({x:null,y:10});
  const dragging=useRef(false);
  const dragOffset=useRef({x:0,y:0});
  const popupRef=useRef(null);

  useEffect(()=>{ if(selectedPosition){ setShowPopup(true); setPopupPos({x:null,y:10}); setChartReady(false); setMintCopied(false); } },[selectedPosition?.mint, selectedPosition?.botWalletId]);

  const onPopupMouseDown=(e)=>{
    if(e.target.tagName==='BUTTON'||e.target.tagName==='SELECT'||e.target.tagName==='INPUT') return;
    dragging.current=true;
    const rect=popupRef.current?.getBoundingClientRect();
    dragOffset.current={x:e.clientX-(rect?.left||0), y:e.clientY-(rect?.top||0)};
    document.body.style.userSelect='none';
  };
  useEffect(()=>{
    const onMove=(e)=>{
      if(!dragging.current||!popupRef.current) return;
      const parent=popupRef.current.parentElement?.getBoundingClientRect();
      if(!parent) return;
      const x=e.clientX-parent.left-dragOffset.current.x;
      const y=e.clientY-parent.top-dragOffset.current.y;
      setPopupPos({x:Math.max(0,Math.min(parent.width-400,x)), y:Math.max(0,Math.min(parent.height-100,y))});
    };
    const onUp=()=>{ dragging.current=false; document.body.style.cursor=''; document.body.style.userSelect=''; };
    window.addEventListener('mousemove',onMove);
    window.addEventListener('mouseup',onUp);
    return()=>{ window.removeEventListener('mousemove',onMove); window.removeEventListener('mouseup',onUp); };
  },[]);

  const [headerImgError, setHeaderImgError] = useState(false);
  const [mintCopied, setMintCopied] = useState(false);
  const copyMint = () => {
    navigator.clipboard?.writeText(selectedPosition.mint);
    setMintCopied(true);
    setTimeout(() => setMintCopied(false), 1500);
  };
  useEffect(()=>setHeaderImgError(false), [selectedPosition?.mint]);
  const isGhost = ghostMints?.has(selectedPosition?.mint+':'+selectedPosition?.botWalletId) || false;
  const positionKeypair = selectedPosition?.botWalletId
    ? (botEngine.keypairMap?.[selectedPosition.botWalletId] || null)
    : botEngine.keypair || null;
  const isSim = selectedPosition?.walletIsSim || false;
  const isVPSPosition = !isSim && !positionKeypair && selectedPosition?.botWalletId && selectedPosition.botWalletId !== 'sim';
  const effectiveKeypair = isSim ? 'sim' : positionKeypair;
  const vpsWalletId = isVPSPosition ? selectedPosition.botWalletId : null;
  const canTrade = isSim || positionKeypair || isVPSPosition;
  const {tradeLoading,msg,executeBuy,executeSell}=useManualTrade(effectiveKeypair, vpsWalletId, onSellSuccess);
  const QUICK=['0.01','0.05','0.1','0.25','0.5','1','2','5'];
  const SELL_PCT=['10%','20%','25%','33%','50%','75%','90%','100%'];

  if(!selectedPosition) return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',gap:12}}>
      <div style={{fontSize:40,opacity:0.2}}></div>
      <div style={{fontSize:14,fontWeight:600,color:'#3a4a5a'}}>Aucune position sélectionnée</div>
      <div style={{fontSize:12,color:'#2a3a4a'}}>Clique sur une position à gauche pour la gérer</div>
    </div>
  );

  const chartUrl=selectedPosition.mint?`https://birdeye.so/tv-widget/${selectedPosition.mint}?chain=solana&viewMode=pair&autoLoad=true&theme=dark`:null;
  const popupStyle = popupPos.x !== null
    ? {position:'absolute',top:popupPos.y,left:popupPos.x,width:380,zIndex:100}
    : {position:'absolute',top:popupPos.y,left:'50%',transform:'translateX(-50%)',width:380,zIndex:100};

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',position:'relative'}}>
      <div style={{padding:'8px 16px',borderBottom:'1px solid #1a2030',display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
        <div style={{width:26,height:26,borderRadius:6,background:isSim?'linear-gradient(135deg,#7c4dff22,#7c4dff11)':'linear-gradient(135deg,#00e5cc22,#7c4dff22)',border:`1px solid ${isSim?'#7c4dff33':'#00e5cc33'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:800,color:isSim?'#7c4dff':'#00e5cc',overflow:'hidden'}}>
          {selectedPosition.logoUrl&&!headerImgError?<img src={selectedPosition.logoUrl} alt="" width={26} height={26} style={{width:'100%',height:'100%',objectFit:'cover'}} onError={()=>setHeaderImgError(true)}/>:(selectedPosition.symbol?.charAt(0)||'?')}
        </div>
        <div style={{flex:1}}>
          <div style={{display:'flex',alignItems:'center',gap:6}}>
            <div style={{fontSize:13,fontWeight:800,color:'#e0e8f0'}}>{selectedPosition.symbol}</div>
            {selectedPosition.walletName&&<Badge variant={isSim?'sim':'live'}>{isSim?'':''} {selectedPosition.walletName}</Badge>}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:4}}>
            <div style={{fontSize:9,color:'#3a4a5a',fontFamily:'monospace'}}>{selectedPosition.mint?.slice(0,20)}...</div>
            <button onClick={copyMint} title="Copier l'adresse du token" style={{background:'none',border:'none',color:mintCopied?'#00e5cc':'#3a4a5a',cursor:'pointer',padding:'0 2px',display:'flex',alignItems:'center',lineHeight:1,transition:'color 0.2s'}}>
              {mintCopied
                ?<svg viewBox="0 0 14 14" fill="none" width="11" height="11"><path d="M2.5 7.5L5.5 10.5L11.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                :<svg viewBox="0 0 14 14" fill="none" width="11" height="11"><rect x="4" y="4" width="8" height="8" rx="1.2" stroke="currentColor" strokeWidth="1.2"/><path d="M2 10V2h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>}
            </button>
          </div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{fontSize:12,fontWeight:800,color:selectedPosition.pnlUsd>=0?'#00e5cc':'#ff6b6b'}}>{selectedPosition.pnlUsd>=0?'+':''}{selectedPosition.pnlUsd?.toFixed(2)}$</span>
          <span style={{fontSize:11,color:selectedPosition.pnlPct>=0?'#00e5cc':'#ff6b6b'}}>{selectedPosition.pnlPct>=0?'+':''}{selectedPosition.pnlPct?.toFixed(1)}%</span>
          <button onClick={()=>setShowPopup(v=>!v)} style={{padding:'5px 12px',borderRadius:5,background:showPopup?'rgba(255,107,107,0.1)':'rgba(0,229,204,0.1)',border:`1px solid ${showPopup?'#ff6b6b33':'#00e5cc33'}`,color:showPopup?'#ff6b6b':'#00e5cc',fontSize:11,fontWeight:700,cursor:'pointer'}}>
            {showPopup?'Fermer':'Gérer'}
          </button>
        </div>
      </div>
      <div style={{flex:1,position:'relative',minHeight:0,overflow:'hidden'}}>
        {chartUrl&&(
          <>
            {!chartReady&&<div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'#0b0e14',zIndex:1}}>
              <div style={{color:'#3a4a5a',fontSize:11}}>Chargement du graphique…</div>
            </div>}
            <iframe src={chartUrl} onLoad={()=>setChartReady(true)} style={{width:'100%',height:'100%',border:'none',display:'block'}} title="Birdeye"/>
          </>
        )}
        {showPopup&&(
          <div ref={popupRef} style={{...popupStyle,background:'rgba(11,14,20,0.97)',border:'1px solid rgba(0,229,204,0.3)',borderRadius:10,padding:'0',boxShadow:'0 8px 32px rgba(0,0,0,0.8)',backdropFilter:'blur(8px)'}}>
            <div onMouseDown={onPopupMouseDown}
              style={{padding:'10px 16px',borderBottom:'1px solid #1a2030',cursor:'grab',display:'flex',alignItems:'center',justifyContent:'space-between',borderRadius:'10px 10px 0 0',background:'rgba(0,229,204,0.04)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <span style={{fontSize:10,color:'#3a4a5a'}}></span>
                <span style={{fontSize:13,fontWeight:800,color:'#e0e8f0'}}>{selectedPosition.symbol}</span>
                <span style={{fontSize:10,color:'#5a6a80'}}>{selectedPosition.mint?.slice(0,8)}...</span>
                <button onClick={e=>{e.stopPropagation();copyMint();}} title="Copier l'adresse du token" style={{background:'none',border:'none',color:mintCopied?'#00e5cc':'#5a6a80',cursor:'pointer',padding:'0 2px',display:'flex',alignItems:'center',lineHeight:1,transition:'color 0.2s'}}>
                  {mintCopied
                    ?<svg viewBox="0 0 14 14" fill="none" width="11" height="11"><path d="M2.5 7.5L5.5 10.5L11.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    :<svg viewBox="0 0 14 14" fill="none" width="11" height="11"><rect x="4" y="4" width="8" height="8" rx="1.2" stroke="currentColor" strokeWidth="1.2"/><path d="M2 10V2h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>}
                </button>
                {selectedPosition.walletName&&<Badge variant={isSim?'sim':'live'}>{isSim?'':''} {selectedPosition.walletName}</Badge>}
              </div>
              <button onClick={()=>setShowPopup(false)} style={{background:'none',border:'none',color:'#5a6a80',cursor:'pointer',fontSize:18,lineHeight:1}}>×</button>
            </div>
            <div style={{padding:'14px 16px'}}>
              {isSim&&<div style={{marginBottom:10,padding:'6px 10px',background:'rgba(124,77,255,0.06)',border:'1px solid #7c4dff22',borderRadius:6,fontSize:10,color:'#7c4dff'}}> Position simulée — vente simulée sans vraie tx</div>}
              {isGhost&&<div style={{marginBottom:10,padding:'8px 10px',background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:6,fontSize:10,color:'#f59e0b',lineHeight:1.7}}>⚠ Position désynchronisée — ce token n'est plus détecté dans le wallet on-chain.<br/><span style={{color:'#5a6a80'}}>Achat et vente désactivés. Vérifie l'historique de ce wallet manuellement.</span></div>}
              {!canTrade&&!isGhost&&<div style={{marginBottom:10,padding:'8px 10px',background:'rgba(255,107,107,0.08)',border:'1px solid rgba(255,107,107,0.25)',borderRadius:6,fontSize:10,color:'#ff8888',lineHeight:1.6}}>⚠ Wallet non identifié pour cette position — trading désactivé.<br/><span style={{color:'#5a6a80'}}>Rafraîchis la page ou vérifie tes configs Copytrade.</span></div>}
              {msg&&<div style={{marginBottom:10}}><TxMsg msg={msg}/></div>}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:10}}>
                <div><div style={{fontSize:8,color:'#3a4a5a',marginBottom:3}}>SLIPPAGE</div>
                  <select value={slippage} onChange={e=>setSlippage(e.target.value)} style={{width:'100%',background:'#0d1117',border:'1px solid #1a2030',borderRadius:4,color:'#8a9ab0',fontSize:10,padding:'4px',outline:'none',cursor:'pointer'}}>
                    <option value="auto">Auto</option><option value="1">1%</option><option value="5">5%</option><option value="10">10%</option>
                  </select>
                </div>
                <div><div style={{fontSize:8,color:'#3a4a5a',marginBottom:3}}>JITO TIP</div>
                  <select value={jitoTip} onChange={e=>setJitoTip(e.target.value)} style={{width:'100%',background:'#0d1117',border:'1px solid #1a2030',borderRadius:4,color:'#8a9ab0',fontSize:10,padding:'4px',outline:'none',cursor:'pointer'}}>
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                  </select>
                </div>
              </div>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:9,color:'#5a6a80',marginBottom:6,letterSpacing:'0.8px'}}>ACHETER (SOL)</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4,marginBottom:6}}>
                  {QUICK.map(q=>(<button key={q} onClick={()=>setAmount(q)} style={{padding:'5px 0',borderRadius:4,background:amount===q?'rgba(0,229,204,0.2)':'#0d1117',border:`1px solid ${amount===q?'#00e5cc44':'#1a2030'}`,color:amount===q?'#00e5cc':'#5a6a80',fontSize:11,fontWeight:700,cursor:'pointer'}}>{q}</button>))}
                </div>
                <button onClick={()=>executeBuy(selectedPosition.mint,amount,slippage,jitoTip)} disabled={tradeLoading||!canTrade||isGhost}
                  style={{width:'100%',padding:'8px',borderRadius:6,background:tradeLoading?'#0d1117':isSim?'linear-gradient(135deg,#7c4dff,#5c2dff)':'linear-gradient(135deg,#00e5cc,#00b8a0)',border:tradeLoading?'1px solid #1a2030':'none',color:tradeLoading?'#5a6a80':'#fff',fontSize:12,fontWeight:800,cursor:tradeLoading||!canTrade||isGhost?'not-allowed':'pointer',opacity:!canTrade||isGhost?0.4:1}}>
                  {tradeLoading==='buy'?'⏳ Achat...':isSim?`Sim Acheter ${amount} SOL`:`Acheter ${amount} SOL`}
                </button>
              </div>
              <div>
                <div style={{fontSize:9,color:'#5a6a80',marginBottom:6,letterSpacing:'0.8px'}}>VENDRE</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4,marginBottom:6}}>
                  {SELL_PCT.map(p=>(<button key={p} onClick={()=>setSellPct(p)} style={{padding:'5px 0',borderRadius:4,background:sellPct===p?'rgba(255,107,107,0.2)':'rgba(255,107,107,0.06)',border:`1px solid ${sellPct===p?'#ff6b6b44':'#ff6b6b22'}`,color:'#ff6b6b',fontSize:10,fontWeight:700,cursor:'pointer'}}>{p}</button>))}
                </div>
                <button onClick={()=>executeSell(selectedPosition.mint,sellPct,slippage,jitoTip)} disabled={tradeLoading||!canTrade||isGhost}
                  style={{width:'100%',padding:'8px',borderRadius:6,background:tradeLoading?'#0d1117':'linear-gradient(135deg,#ff6b6b,#cc4444)',border:tradeLoading?'1px solid #1a2030':'none',color:tradeLoading?'#5a6a80':'#fff',fontSize:12,fontWeight:800,cursor:tradeLoading||!canTrade||isGhost?'not-allowed':'pointer',opacity:!canTrade||isGhost?0.4:1}}>
                  {tradeLoading==='sell'?'⏳ Vente...':isSim?`Sim Vendre ${sellPct}`:`Vendre ${sellPct}`}
                </button>
              </div>
              <div style={{marginTop:10,padding:'8px',background:isGhost?'rgba(245,158,11,0.04)':'rgba(0,229,204,0.04)',border:`1px solid ${isGhost?'rgba(245,158,11,0.15)':'#00e5cc11'}`,borderRadius:6,fontSize:10,display:'flex',justifyContent:'space-between'}}>
                <span style={{color:isGhost?'#f59e0b':'#5a6a80'}}>{isGhost?'⚠ Désynchronisé':isVPSPosition?'VPS Live':isSim?'Simulation':'Live'}</span>
                <span style={{color:selectedPosition.pnlUsd>=0?'#00e5cc':'#ff6b6b',fontWeight:700}}>PNL: {selectedPosition.pnlUsd>=0?'+':''}{(selectedPosition.pnlUsd||0).toFixed(2)}$</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div style={{height:80,borderTop:'1px solid #1a2030',display:'flex',flexDirection:'column',flexShrink:0}}>
        <div style={{display:'flex',borderBottom:'1px solid #1a2030'}}>
          {['trades','holders','top_traders'].map(t=>(
            <button key={t} onClick={()=>setTradeTab(t)} style={{padding:'6px 16px',fontSize:11,fontWeight:700,cursor:'pointer',background:'transparent',border:'none',borderBottom:`2px solid ${tradeTab===t?'#00e5cc':'transparent'}`,color:tradeTab===t?'#00e5cc':'#4a5a70'}}>
              {t==='top_traders'?'Top Traders':t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>
        <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:'#3a4a5a'}}>
          <span style={{fontSize:11}}>Aucun trade trouvé</span>
        </div>
      </div>
    </div>
  );
}

export default function CopytradeTab({ walletAddress, curators=[], onGoToNetwork, botWallet: botWalletProp, pendingCopyWallet=null, onPendingCopyWalletUsed=null }) {
  const { effectiveUserId, isImpersonating } = useEffectiveUser();
  const [configs,setConfigs]=useState(loadConfigs);
  const [selectedId,setSelectedId]=useState(null);
  const [selectedPosition,setSelectedPosition]=useState(null);
  const [showModal,setShowModal]=useState(false);
  const [editConfig,setEditConfig]=useState(null);
  const [subTab,setSubTab]=useState('configs');
  const [botStarted,setBotStarted]=useState(false);
  const [botStats,setBotStats]=useState(getBotStats());
  const [leftOpen,setLeftOpen]=useState(true);

  const [leftWidth,setLeftWidth]=useState(260);

  const [alerts,setAlerts]=useState([]);
  const [allTrades,setAllTrades]=useState(()=>JSON.parse(localStorage.getItem('bot-trades')||'[]'));
  const [saveError,setSaveError]=useState(null);
  const [isSaving,setIsSaving]=useState(false);
  const [livePnl,setLivePnl]=useState(null);

//  Charger les configs depuis Supabase au démarrage
  useEffect(() => {
    loadConfigsFromSupabase(effectiveUserId).then(supabaseConfigs => {
      if (supabaseConfigs && supabaseConfigs.length > 0) {
        setConfigs(supabaseConfigs);
        if (!isImpersonating) localStorage.setItem(STORAGE_KEY, JSON.stringify(supabaseConfigs));
      }
    });
  }, [effectiveUserId]);

  const draggingLeft=useRef(false);

  const bodyRef=useRef(null);
  const prevTradesRef=useRef([]);

  const { wallet, walletList, balances, keypairs } = useBotWallet();
  const activeWallet = botWalletProp || wallet;

  const activeConfigs=configs.filter(c=>c.enabled&&c.targetWallet&&c.targetWallet.length>10);
  const activeCount=activeConfigs.length;

  const [ghostMints, setGhostMints] = useState(new Set());
  const allTradesRef = useRef(allTrades);
  useEffect(() => { allTradesRef.current = allTrades; }, [allTrades]);

  useEffect(() => {
    if (!pendingCopyWallet) return;
    const tierLabel = pendingCopyWallet.tier === 'sniper' ? 'Sniper' : 'Early';
    const addr = pendingCopyWallet.wallet_address;
    const shortAddr = addr.slice(0, 6) + '...' + addr.slice(-4);
    setEditConfig({ targetWallet: addr, name: `${tierLabel} · ${shortAddr}` });
    setShowModal(true);
    if (onPendingCopyWalletUsed) onPendingCopyWalletUsed();
  }, [pendingCopyWallet]);

  const handleSellSuccess = useCallback((tokenMint, isSim, pct = 100) => {
    setAllTrades(prev => prev.map(t => {
      if (!(t.mint === tokenMint && t.resultat === 'En cours' && t.isSim === isSim)) return t;
      if (pct >= 100) {
        return { ...t, resultat: isSim ? 'Fermé' : 'Vendu', sortieTs: Math.floor(Date.now() / 1000) };
      }
      const factor = 1 - pct / 100;
      return { ...t, tokenAmount: (t.tokenAmount || 0) * factor, achatUsd: (t.achatUsd || 0) * factor };
    }));
  }, []);

  const vpsWalletCount = walletList.filter(w => w.fromVPS && !w.isSim).length;

  useEffect(() => {
    if (!vpsWalletCount) return;
    let cancelled = false;
    const checkOnChain = async () => {
      const heliusKey = localStorage.getItem('helius-api-key') || '';
      const rpcUrl = heliusKey
        ? `https://mainnet.helius-rpc.com/?api-key=${heliusKey}`
        : 'https://api.mainnet-beta.solana.com';
      const vpsWallets = walletList.filter(w => w.fromVPS && !w.isSim && w.address);
      const allOpen = allTradesRef.current.filter(t => t.resultat === 'En cours' && !t.isSim);
      if (!allOpen.length) { if (!cancelled) setGhostMints(new Set()); return; }
      const openKnown   = allOpen.filter(t =>  t.botWalletId);
      const openUnknown = allOpen.filter(t => !t.botWalletId);
      const newGhosts = new Set();
      const unionMints = new Set();
      for (const vw of vpsWallets) {
        try {
          const r = await fetch(rpcUrl, {
            method: 'POST', headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({jsonrpc:'2.0',id:1,method:'getTokenAccountsByOwner',
              params:[vw.address,{programId:'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'},{encoding:'jsonParsed'}]})
          });
          const j = await r.json();
          const onChain = new Set(
            (j?.result?.value||[]).map(a=>a?.account?.data?.parsed?.info)
              .filter(i=>i&&parseFloat(i.tokenAmount?.uiAmount||0)>0).map(i=>i.mint)
          );
          openKnown.filter(t => t.botWalletId === vw.id).forEach(t => {
            if (!onChain.has(t.mint)) newGhosts.add(t.mint + ':' + t.botWalletId);
          });
          onChain.forEach(mint => unionMints.add(mint));
        } catch {}
      }
      openUnknown.forEach(t => {
        if (!unionMints.has(t.mint)) {
          const matchCfg = configs?.find(c => c.targetWallet === t.walletSuivi);
          const renderedBotWalletId = matchCfg?.botWalletId || null;
          newGhosts.add(t.mint + ':' + renderedBotWalletId);
        }
      });
      if (!cancelled) setGhostMints(newGhosts);
    };
    checkOnChain();
    const iv = setInterval(checkOnChain, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vpsWalletCount]);

useEffect(()=>{
    //  Charger les trades depuis Supabase au démarrage
    const loadTradesFromSupabase = async () => {
      try {
        const merged = await loadAndMergeTradesFromSupabase(effectiveUserId, isImpersonating);
        if (merged !== null) {
          setAllTrades(merged);
          prevTradesRef.current = merged;
          if (merged.length === 0) setBotStats({ pnlUsd:0, pnlPct:0, openTrades:0, totalInvested:0, positions:[] });
        }
      } catch(e) { console.warn('Erreur chargement trades:', e.message); }
    };
    loadTradesFromSupabase();

    let tickCount = 0;
    const iv=setInterval(()=>{
      setBotStats(getBotStats());
      tickCount++;
      // Polling Supabase toutes les 10s (every 5 ticks of 2s)
      if (tickCount % 5 === 0) {
        loadTradesFromSupabase();
        return;
      }
      if (isImpersonating) return;
      const trades=JSON.parse(localStorage.getItem('bot-trades')||'[]');
      const prev=prevTradesRef.current;
      const openChanged=trades.filter(t=>t.resultat==='En cours').length!==prev.filter(t=>t.resultat==='En cours').length;
      if(trades.length!==prev.length||openChanged||trades.some(t=>!prev.find(p=>p.id===t.id))) setAllTrades(trades);
      const newTrades=trades.filter(t=>!prev.find(p=>p.id===t.id));
      if(newTrades.length&&prev.length>0){
        const newAlerts=newTrades.map(t=>({
          type: t.resultat==='En cours'?'buy':'sell',
          symbol: t.symbol, isSim: t.isSim||false,
          detail: t.resultat==='En cours'?`${(t.achatUsd||0).toFixed(2)}$ investi`:`PNL: ${(t.pnlUsd||0)>=0?'+':''}${(t.pnlUsd||0).toFixed(2)}$`,
        }));
        setAlerts(prev=>[...prev,...newAlerts].slice(-5));
      }
      prevTradesRef.current=trades;
    },2000);

    // Abonnement Supabase temps réel — mise à jour instantanée dès qu'un trade est créé/modifié
    let realtimeSub = null;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return;
      const targetId = effectiveUserId || session.user.id;
      realtimeSub = supabase.channel(`copytrade-trades-${targetId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'trades', filter: `user_id=eq.${targetId}` }, () => {
          loadTradesFromSupabase();
        })
        .subscribe();
    });

    return()=>{ clearInterval(iv); if(realtimeSub) supabase.removeChannel(realtimeSub); };
  },[effectiveUserId]);

  useEffect(()=>{ setLivePnl(null); },[subTab]);

  useEffect(()=>{
    const onMove=(e)=>{
      if(!bodyRef.current) return;
      const rect=bodyRef.current.getBoundingClientRect();
      const x=e.clientX-rect.left;
      if(draggingLeft.current)  setLeftWidth(Math.max(160,Math.min(520,x)));
    };
    const onUp=()=>{ draggingLeft.current=false; document.body.style.cursor=''; document.body.style.userSelect=''; };
    window.addEventListener('mousemove',onMove);
    window.addEventListener('mouseup',onUp);
    return()=>{ window.removeEventListener('mousemove',onMove); window.removeEventListener('mouseup',onUp); };
  },[]);

  const startDragLeft=()=>{ draggingLeft.current=true; document.body.style.cursor='col-resize'; document.body.style.userSelect='none'; };
  const dismissAlert=(i)=>setAlerts(prev=>prev.filter((_,idx)=>idx!==i));

  const toggleConfig=(id)=>{
    if (isImpersonating) return;
    const config=configs.find(c=>c.id===id);
    const willEnable=!config?.enabled;
    const next=configs.map(c=>c.id===id?{...c,enabled:!c.enabled}:c);
    setConfigs(next); saveConfigs(next, setSaveError, setIsSaving);
    if(willEnable&&config?.targetWallet&&config.targetWallet.length>10) {
      const configWallet = walletList.find(w => w.id === config.botWalletId);
      const isSim = configWallet?.isSim || false;
      if(isSim) {
        botEngine.start('', next.filter(c=>c.enabled&&c.targetWallet&&c.targetWallet.length>10), (stats)=>{if(stats)setBotStats(stats);}, walletList)
          .then(()=>setBotStarted(true)).catch(console.error);
      } else {
        // Wallet live : le VPS gère l'exécution, pas besoin de mot de passe
        setBotStarted(true);
      }
    }
    else if(botStarted) botEngine.configs=next.filter(c=>c.enabled&&c.targetWallet&&c.targetWallet.length>10);
  };

const saveConfig=(data)=>{
    if (isImpersonating) return;
    const next=editConfig
      ?configs.map(c=>c.id===editConfig.id?{...c,...data,enabled:data.status??data.enabled??c.enabled}:c)
      :[...configs,{id:nextConfigId++,name:data.name||'Config',type:'manual',balance:data.amountValue||'0.1',address:data.targetWallet||'N/A',...data,enabled:data.status??false}];
    setConfigs(next); saveConfigs(next, setSaveError, setIsSaving); setShowModal(false); setEditConfig(null);
  };

  const deleteConfig=(id)=>{
    if (isImpersonating) return;
    const next=configs.filter(c=>c.id!==id);
    setConfigs(next); saveConfigs(next, setSaveError, setIsSaving);
    if(selectedId===id) setSelectedId(null);
  };

  const selected=configs.find(c=>c.id===selectedId);

  const Divider=({onMouseDown})=>(
    <div onMouseDown={onMouseDown}
      style={{width:12,flexShrink:0,background:'#090c12',borderLeft:'1px solid #1a2030',borderRight:'1px solid #1a2030',cursor:'col-resize',display:'flex',alignItems:'center',justifyContent:'center',position:'relative',zIndex:10}}
      onMouseEnter={e=>e.currentTarget.style.background='rgba(0,229,204,0.05)'}
      onMouseLeave={e=>e.currentTarget.style.background='#090c12'}>
      <button onClick={()=>setLeftOpen(v=>!v)} onMouseDown={e=>e.stopPropagation()}
        style={{width:12,height:36,borderRadius:3,background:'#1a2030',border:'1px solid #2a3040',color:'#5a6a80',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,padding:0}}>
        {leftOpen?'‹':'›'}
      </button>
    </div>
  );

  return (
    <div className="ct-root">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes slideIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
      `}</style>

      <InAppAlert alerts={alerts} onDismiss={dismissAlert}/>
      {saveError&&(
        <div onClick={()=>setSaveError(null)} style={{position:'fixed',top:70,left:'50%',transform:'translateX(-50%)',zIndex:3000,background:'rgba(200,50,50,0.97)',border:'1px solid #ff6b6b',borderRadius:8,padding:'8px 16px',cursor:'pointer',fontSize:11,color:'#fff',fontWeight:700,boxShadow:'0 4px 16px rgba(0,0,0,0.5)',maxWidth:460,textAlign:'center'}}>
          ⚠ {saveError} ×
        </div>
      )}


      {showModal&&<ConfigModal initial={editConfig} onSave={saveConfig} onClose={()=>{setShowModal(false);setEditConfig(null);}} botWallet={activeWallet} walletList={walletList} balances={balances} isSaving={isSaving}/>}

      <div className="ct-stats-bar">
        {(()=>{
          const openCount=allTrades.length>0?allTrades.filter(t=>t.resultat==='En cours').length:null;
          const pnl=livePnl!==null?livePnl.pnlUsd:allTrades.length>0?(openCount>0?botStats.pnlUsd:0):botStats.pnlUsd;
          const pct=livePnl!==null?livePnl.pnlPct:allTrades.length>0?(openCount>0?botStats.pnlPct:0):botStats.pnlPct;
          return <div className="ct-stat"><span className="ct-stat-lbl">PNL</span><span className={`ct-stat-val ${pnl>=0?'ct-positive':'ct-negative'}`}>{pnl>=0?'+':''}{pnl.toFixed(1)}$ ({pct>=0?'+':''}{pct.toFixed(1)}%)</span></div>;
        })()}
        <span className="ct-bar-sep"/>
        <div className="ct-stat"><span className="ct-stat-lbl">OUVERT</span><span className="ct-stat-val">{allTrades.length>0?allTrades.filter(t=>t.resultat==='En cours').length:botStats.openTrades}</span></div>
        <span className="ct-bar-sep"/>
        <div className="ct-stat"><span className="ct-stat-lbl">INVESTI</span><span className="ct-stat-val">${(allTrades.length>0?allTrades.filter(t=>t.resultat==='En cours').reduce((s,t)=>s+(t.achatUsd||0),0):botStats.totalInvested).toFixed(0)}</span></div>
        <span className="ct-bar-sep"/>
        <div className="ct-stat"><span className="ct-stat-lbl">CONFIGS ACTIVES</span><span className="ct-stat-val">{activeCount}</span></div>
        <span className="ct-bar-sep"/>
        <div className="ct-stat">
          <span style={{display:'flex',alignItems:'center',gap:5}}>
            <LiveDot color={activeCount>0?'accent':'inactive'} size={7} pulse={activeCount>0} />
            <span style={{color:activeCount>0?'#00e5cc':'#5a6a80',fontWeight:700,fontSize:10}}>{activeCount>0?'BOT ACTIF':'BOT EN VEILLE'}</span>
          </span>
        </div>
        <div className="ct-stat-spacer"/>
      </div>

      <div className="ct-subtabs">
        {[['configs','Configs'],['positions','Positions'],['analytics','Analytics']].map(([id,label])=>(
          <button key={id} className={`ct-subtab ${subTab===id?'active':''}`} onClick={()=>setSubTab(id)}>{label}</button>
        ))}
      </div>

      <div ref={bodyRef} className="ct-body"style={{display:'flex',flex:1,overflow:'hidden',position:'relative'}}>
        {leftOpen&&subTab!=='analytics'&&(
          <div style={{width:leftWidth,minWidth:160,maxWidth:520,flexShrink:0,display:'flex',flexDirection:'column',background:'#090c12',overflow:'hidden'}}>
            {subTab==='configs'&&<ConfigsPanel configs={configs} selectedId={selectedId} setSelectedId={setSelectedId} toggleConfig={toggleConfig} setEditConfig={setEditConfig} setShowModal={setShowModal} deleteConfig={deleteConfig} onGoToNetwork={onGoToNetwork}/>}
            {subTab==='positions'&&<PositionsPanel botStats={botStats} selectedPosition={selectedPosition} onSelectPosition={setSelectedPosition} allTrades={allTrades} configs={configs} ghostMints={ghostMints} onLivePnl={setLivePnl}/>}
          </div>
        )}
        {subTab!=='analytics'&&<Divider onMouseDown={leftOpen?startDragLeft:undefined}/>}
        <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minWidth:0}}>
          {subTab==='configs'&&(selected?(
            <div className="ct-position-detail">
              <div className="ct-detail-name">{selected.emoji||''} {selected.name}</div>
              <div className="ct-detail-addr">{selected.targetWallet||selected.address}</div>
              <div style={{display:'flex',gap:8,marginTop:12,flexWrap:'wrap',justifyContent:'center'}}>
                {selected.enabled&&selected.targetWallet&&<div style={{fontSize:11,color:'#00e5cc',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc22',borderRadius:4,padding:'4px 10px'}}>🟢 En écoute</div>}
                {selected.amountValue&&<div style={{fontSize:11,color:'#5a6a80',background:'#0d1117',border:'1px solid #1a2030',borderRadius:4,padding:'4px 10px'}}> {selected.amountValue} {selected.amountMode!=='fixed'?'%':'SOL'}</div>}
                {selected.jitoTip&&<div style={{fontSize:11,color:'#7c4dff',background:'#0d1117',border:'1px solid #7c4dff22',borderRadius:4,padding:'4px 10px'}}> Jito {selected.jitoTip}</div>}
                {selected.copySells&&<div style={{fontSize:11,color:'#00e5cc',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc22',borderRadius:4,padding:'4px 10px'}}> Copie les ventes</div>}
                {selected.stopLossEnabled!==false&&selected.stopLoss&&<div style={{fontSize:11,color:'#ff6b6b',background:'rgba(255,107,107,0.08)',border:'1px solid #ff6b6b22',borderRadius:4,padding:'4px 10px'}}> Stop-loss -{selected.stopLoss}%</div>}
                {selected.takeProfitEnabled!==false&&selected.takeProfit&&<div style={{fontSize:11,color:'#00e5cc',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc22',borderRadius:4,padding:'4px 10px'}}>🟢 Take-profit +{selected.takeProfit}%</div>}
                {selected.botWalletId&&walletList.find(w=>w.id===selected.botWalletId)&&(
                  <div style={{fontSize:11,color:'#7c4dff',background:'rgba(124,77,255,0.08)',border:'1px solid #7c4dff22',borderRadius:4,padding:'4px 10px'}}>
                    {walletList.find(w=>w.id===selected.botWalletId)?.isSim?'':''} {walletList.find(w=>w.id===selected.botWalletId)?.name}
                  </div>
                )}
              </div>
            </div>
          ):(
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100%',color:'#3a4a5a',gap:12}}>
              <div style={{fontSize:40,opacity:0.2}}></div>
              <div style={{fontSize:14,fontWeight:600,color:'#3a4a5a'}}>Sélectionne une config</div>
              <div style={{fontSize:12,color:'#2a3a4a'}}>ou crée-en une nouvelle à gauche</div>
            </div>
          ))}
          {subTab==='positions'&&<MonitorCenterPanel selectedPosition={selectedPosition} keypairs={keypairs} configs={configs} ghostMints={ghostMints} onSellSuccess={handleSellSuccess}/>}
          {subTab==='analytics'&&<AnalyticsPanel trades={allTrades}/>}
        </div>
      </div>
    </div>
  );
}